import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import {
  addRemarks,
  callFromWeb,
  checkTodaysCallLogs,
  findLeadByPhoneNumber,
  getLogsCount,
  getTelecallerLead,
  addCallLogs,
  updateAndFetchNextLead,
  getTodayDateForTelecaller,
  fetchCallLogs,
  setCurrentLeadAndShouldAddCallLogTrue,
  setCurrentLead,
} from "../../store/slices/telecallerLeadSlice";
import EditLead from "../../pages/LeadDetails/editLead/EditLead";
import toast from "react-hot-toast";
import { getRemarks } from "../../store/slices/telecallerLeadSlice";
import { fetchKanbanData } from "../../store/slices/kanbanSlice";
import { startSession } from "../../store/slices/telecallerSessionSlice";
import { motion, AnimatePresence } from "framer-motion";
import socket from "../../utils/socketConnect.utils";
import { getUserId } from "../../utils/auth";
import { CallType, ICallInfo, ICallLog } from "../../models/Call.model";
import TelecallerBoardSkeleton from "../../components/Skeleton/TelecallerBoardSkeleton";
import { CallLogs } from "./CallLogs";
import Remarks from "./Remarks";
import ILeadData, { ILeadUpdateData } from "../../models/LeadData.model";
import RemarkModal from "./AddCallRemarkModal";
import CallInprogressToast from "./CallInprogressToast";
import StartSession from "./StartSession";
import SessionInactivePage from "./SessionIncativePage";

type RemarkStackItem = { lead: ILeadData; callLog: ICallLog };

const STORAGE_KEYS = {
  IS_MODAL_OPEN: "isCallLogModalOpen",
  PENDING_LEAD: "pendingCallLogLead",
  PENDING_CALL_LOG: "pendingCallLog",
  INCOMING_STACK: "incomingLeadStack",
};
const TelecallerBoard: React.FC = () => {
  const [isRemarkModalOpen, setIsRemarkModalOpen] = useState(false);
  const [incomingLeadStack, setIncomingLeadStack] = useState<RemarkStackItem[]>(
    []
  );
  const [latestCallLog, setLatestCallLog] = useState<ICallLog | null>(null);
  const [callRemarks, setCallRemark] = useState("");
  const [remarkText, setRemarkText] = useState("");
  const [skipped, setSkipped] = useState(false);
  const [notInterested, setNotInterested] = useState(false);
  const [notPickedUp, setNotPickedUp] = useState(false);
  const [isCallInProgress, setIsCallInProgress] = useState(false);
  const [showToBDE, setShowToBDE] = useState<boolean | null>(null);
  const [agreedToMeet, setAgreedToMeet] = useState<boolean | null>(null);
  const [showOnDate, setShowOnDate] = useState<Date | null>(null);
  const processingCalls = useRef(new Set<string>());

  const createCallLogId = (data: ICallLog, prefix: string = "call") => {
    const processingTime = Date.now();
    const id = `${prefix}-${data.number}-${data.startTime}-${data.type}-${processingTime}`;
    return id;
  };

  const dispatch: RootDispatch = useDispatch();
  const { currentLead, userLogsCount } = useSelector(
    (state: RootState) => state.telecallerLead
  );

  const remarks = useSelector(
    (state: RootState) => state.telecallerLead.currentLead?.remarks
  );

  const sessionActive = useSelector(
    (state: RootState) => state.telecallerSession.active
  );

  const callLogs = useSelector(
    (state: RootState) => state.telecallerLead.callLogs
  );

  const [mobileNumber, setMobileNumber] = useState<string | null>(
    currentLead?.mobileNumber || null
  );

  const fetchingLead = useSelector(
    (state: RootState) =>
      state.telecallerLead.loadingAndErrorState.fetchingTelecallerLead
  );
  const callingToLeadLoading = useSelector(
    (state: RootState) =>
      state.telecallerLead.loadingAndErrorState.callingtoLead
  );

  const userId = getUserId();

  const saveCallLogStateToStorage = (lead: ILeadData, callLog: ICallLog) => {
    try {
      localStorage.setItem(STORAGE_KEYS.IS_MODAL_OPEN, "true");
      localStorage.setItem(STORAGE_KEYS.PENDING_LEAD, JSON.stringify(lead));
      localStorage.setItem(
        STORAGE_KEYS.PENDING_CALL_LOG,
        JSON.stringify(callLog)
      );
    } catch (error) {
      console.error("Failed to save call log state to localStorage:", error);
    }
  };

  const saveIncomingStackToStorage = (stack: RemarkStackItem[]) => {
    try {
      localStorage.setItem(STORAGE_KEYS.INCOMING_STACK, JSON.stringify(stack));
    } catch (error) {
      console.error("Failed to save incoming stack to localStorage:", error);
    }
  };

  const clearCallLogStateFromStorage = () => {
    try {
      Object.values(STORAGE_KEYS).forEach((key) => {
        localStorage.removeItem(key);
      });
    } catch (error) {
      console.error("Failed to clear call log state from localStorage:", error);
    }
  };

  const handleModalClose = () => {
    setIsRemarkModalOpen(false);
    clearCallLogStateFromStorage();
    setLatestCallLog(null);
    setIncomingLeadStack([]);
    setCallRemark("");
    setShowOnDate(null);
  };

  const restoreCallLogStateFromStorage = () => {
    try {
      const isModalOpen = localStorage.getItem(STORAGE_KEYS.IS_MODAL_OPEN);
      const pendingLeadStr = localStorage.getItem(STORAGE_KEYS.PENDING_LEAD);
      const pendingCallLogStr = localStorage.getItem(
        STORAGE_KEYS.PENDING_CALL_LOG
      );
      const incomingStackStr = localStorage.getItem(
        STORAGE_KEYS.INCOMING_STACK
      );

      if (isModalOpen === "true" && pendingLeadStr && pendingCallLogStr) {
        const pendingLead = JSON.parse(pendingLeadStr) as ILeadData;
        const pendingCallLog = JSON.parse(pendingCallLogStr) as ICallLog;

        dispatch(setCurrentLead(pendingLead));

        setMobileNumber(pendingLead.mobileNumber || null);

        setLatestCallLog(pendingCallLog);
        setIsRemarkModalOpen(true);

        if (incomingStackStr) {
          const restoredStack = JSON.parse(
            incomingStackStr
          ) as RemarkStackItem[];
          setIncomingLeadStack(restoredStack);
        }

        if (pendingLead._id) {
          dispatch(fetchCallLogs(pendingLead._id));
          dispatch(getRemarks(pendingLead._id));
        }

        return true;
      }
      return false;
    } catch (error) {
      console.error(
        "Failed to restore call log state from localStorage:",
        error
      );
      clearCallLogStateFromStorage();
      return false;
    }
  };

  const formatPhoneNumber = (number: string): string => {
    return number?.replace(/^\+91/, "");
  };

  const validateCallLogFields = (data: ICallLog): boolean => {
    const requiredFields: (keyof ICallLog)[] = [
      "callDate",
      "duration",
      "endTime",
      "startTime",
      "number",
      "type",
      "wasPickedUp",
    ];

    return requiredFields.every((field) => data[field] != null);
  };

  const createCallLogObject = (
    data: ICallLog,
    leadId: string,
    callType: CallType
  ): ICallLog => {
    const formattedNumber = formatPhoneNumber(data.number || "");

    return {
      callDate: data.callDate,
      duration: data.duration,
      endTime: data.endTime,
      startTime: data.startTime,
      type: callType,
      wasPickedUp: data.wasPickedUp,
      leadId: leadId,
      _id: data._id,
      remark: data.remark || "",
      number: formattedNumber,
    };
  };

  const addToStackAndShowModal = useCallback(
    (lead: ILeadData, callLog: ICallLog) => {
      setLatestCallLog(callLog);
      setIsRemarkModalOpen(true);

      saveCallLogStateToStorage(lead, callLog);

      setIncomingLeadStack((prev) => {
        const updatedStack = [...prev, { lead, callLog }];
        saveIncomingStackToStorage(updatedStack);
        return updatedStack;
      });
    },
    []
  );

  const handleUnpickedOutgoingCall = useCallback(
    async (data: ICallLog, leadId?: string) => {
      // Create a more specific ID for unpicked calls
      const callId = `${Date.now()}-unpicked-${data.number}-${data.startTime}-${
        data.type
      }`;

      if (processingCalls.current.has(callId)) {
        console.log("Unpicked call already being processed:", callId);
        return;
      }

      processingCalls.current.add(callId);

      try {
        const callLogToSave = createCallLogObject(
          data,
          leadId || currentLead?._id || "",
          CallType.OUTGOING_TYPE
        );

        callLogToSave.remark = "call not picked up";

        await dispatch(
          addCallLogs({
            callLogs: [callLogToSave],
            leadId: callLogToSave.leadId,
          })
        ).unwrap();

        toast.success("Call not picked up - logged successfully");
      } catch (error) {
        console.error("Failed to save unpicked call log:", error);
        toast.error("Failed to save call not picked up remark");
      } finally {
        // Use setTimeout to ensure the ID stays in the set for a bit longer
        setTimeout(() => {
          processingCalls.current.delete(callId);
        }, 1000);
      }
    },
    [dispatch, currentLead?._id]
  );

  const handleIncomingCall = useCallback(
    async (data: ICallLog) => {
      const callId = createCallLogId(data);

      if (processingCalls.current.has(callId)) {
        return;
      }

      processingCalls.current.add(callId);
      try {
        const formattedNumber = formatPhoneNumber(data.number || "");

        const incomingLead = await dispatch(
          findLeadByPhoneNumber(formattedNumber)
        ).unwrap();

        if (!incomingLead) {
          toast.error("No lead found for this number");
          return;
        }

        dispatch(setCurrentLeadAndShouldAddCallLogTrue(incomingLead));

        const callLogWithId = createCallLogObject(
          data,
          incomingLead._id ?? "",
          CallType.INCOMING_TYPE
        );

        addToStackAndShowModal(incomingLead, callLogWithId);

        toast.success(
          `Incoming call from ${incomingLead.name || formattedNumber}`
        );
      } catch (error) {
        console.error("Error processing incoming call:", error);
        toast.error("Error processing incoming call");
        setIsCallInProgress(false);
      } finally {
        processingCalls.current.delete(callId);
      }
    },
    [dispatch, addToStackAndShowModal]
  );

  const handleOutgoingCall = useCallback(
    async (data: ICallLog) => {
      if (!currentLead) {
        console.warn("No current lead available for outgoing call");
        return;
      }

      if (!validateCallLogFields(data)) {
        console.warn("Invalid call log fields");
        return;
      }

      // Create a consistent call ID
      const callId = `outgoing-${data.number}-${data.startTime}-${data.type}`;

      if (processingCalls.current.has(callId)) {
        console.log("Outgoing call already being processed:", callId);
        return;
      }

      processingCalls.current.add(callId);

      try {
        if (data.wasPickedUp === false) {
          // For unpicked calls, don't process further in this function
          // Let handleUnpickedOutgoingCall handle it completely
          await handleUnpickedOutgoingCall(data);
        } else if (data.wasPickedUp === true) {
          const callLogWithId = createCallLogObject(
            data,
            currentLead._id ?? "",
            CallType.OUTGOING_TYPE
          );

          addToStackAndShowModal(currentLead, callLogWithId);
        }
      } catch (error) {
        console.error("Error handling outgoing call:", error);
        toast.error("Error processing outgoing call");
      } finally {
        // Delay removal to prevent race conditions
        setTimeout(() => {
          processingCalls.current.delete(callId);
        }, 1000);
      }
    },
    [currentLead, handleUnpickedOutgoingCall, addToStackAndShowModal]
  );

  const handleMobileInitiatedCall = useCallback(
    async (msg: any) => {
      try {
        if (msg.data.userId === getUserId()) {
          const lead = await dispatch(
            findLeadByPhoneNumber(msg?.data?.mobileNumber)
          ).unwrap();

          if (lead) {
            dispatch(setCurrentLead(lead));
            setIsRemarkModalOpen(true);
            saveCallLogStateToStorage(lead, {} as ICallLog);
            toast.success("Lead found and set for mobile call");
          }
        }
      } catch (error) {
        console.warn("❌ No lead found for mobile initiated call:", error);
        toast.error("No lead found for this mobile number");
      }
    },
    [dispatch]
  );

  const handleCallLogEvent = useCallback(
    async (data: ICallLog) => {
      setIsCallInProgress(false);

      switch (data.type) {
        case CallType.INCOMING_TYPE:
          await handleIncomingCall(data);
          break;

        case CallType.OUTGOING_TYPE:
          await handleOutgoingCall(data);
          break;

        default:
          await handleOutgoingCall(data);
          break;
      }
    },
    [handleIncomingCall, handleOutgoingCall]
  );

  useEffect(() => {
    const newCallLogEventName = "new-call-log" + userId;
    const mobileCallEventName = "calling-lead-from-mobile";

    socket.on(newCallLogEventName, handleCallLogEvent);
    socket.on(mobileCallEventName, handleMobileInitiatedCall);

    return () => {
      socket.off(newCallLogEventName, handleCallLogEvent);
      socket.off(mobileCallEventName, handleMobileInitiatedCall);
    };
  }, [handleCallLogEvent, handleMobileInitiatedCall, userId]);

  useEffect(() => {
    const eventName = `callLogUpdated${userId}`;

    const handleCallLogUpdate = async (msg: string) => {
      if (msg && currentLead?._id) {
        await dispatch(fetchCallLogs(currentLead._id));
        await dispatch(getLogsCount({}));
      }
    };

    socket.on(eventName, handleCallLogUpdate);

    return () => {
      socket.off(eventName, handleCallLogUpdate);
    };
  }, [currentLead?._id, dispatch, userId]);

  useEffect(() => {
    setMobileNumber(currentLead?.mobileNumber || null);
  }, [currentLead]);

  useEffect(() => {
    const handleFetchCallLogs = async () => {
      const leadId = currentLead?._id;
      if (leadId) {
        await dispatch(fetchCallLogs(leadId));
      }
    };
    dispatch(getTodayDateForTelecaller());
    handleFetchCallLogs();
  }, [dispatch, currentLead?._id]);

  useEffect(() => {
    dispatch(checkTodaysCallLogs(mobileNumber || ""));
  }, [dispatch, mobileNumber, callLogs]);

  useEffect(() => {
    if (!socket.connected) {
      socket.connect();
    }
  }, []);

  const data: ICallInfo = {
    leadName: currentLead?.name || "",
    contactPersonName: currentLead?.contactPersonName || "",
    mobileNumber: mobileNumber || "",
  };

  useEffect(() => {
    const hasRestoredState =
      localStorage.getItem(STORAGE_KEYS.IS_MODAL_OPEN) === "true";

    if (hasRestoredState && !currentLead) {
      restoreCallLogStateFromStorage();
    }
  }, [dispatch, currentLead]);

  const callToNumber = async () => {
    try {
      setIsCallInProgress(false);
      const res = await dispatch(callFromWeb(data));

      if (res.meta.requestStatus === "fulfilled") {
        setIsCallInProgress(true);
        toast.success("Call initiated successfully");
      } else {
        toast.error("Failed to initiate call");
      }
    } catch (error) {
      console.error("Error initiating call:", error);
      toast.error("Error initiating call");
      setIsCallInProgress(false);
    }
  };

  const popFromIncomingLeadStack = () => {
    setIncomingLeadStack((prev) => {
      const updatedStack = [...prev];
      updatedStack.pop();

      if (updatedStack.length > 0) {
        const nextItem = updatedStack[updatedStack.length - 1];
        dispatch(setCurrentLead(nextItem.lead));
        setLatestCallLog(nextItem.callLog);
        saveIncomingStackToStorage(updatedStack);
        saveCallLogStateToStorage(nextItem.lead, nextItem.callLog);
        setIsRemarkModalOpen(true);
      } else {
        setLatestCallLog(null);
        setIsRemarkModalOpen(false);
        clearCallLogStateFromStorage();
      }
      return updatedStack;
    });
  };

  const saveRemarkWithCallLog = async () => {
    try {
      const callLogToSave = {
        callDate: latestCallLog?.callDate || new Date().toISOString(),
        duration: String(latestCallLog?.duration ?? 0),
        endTime: latestCallLog?.endTime || new Date().toISOString(),
        startTime: latestCallLog?.startTime || new Date().toISOString(),
        type: latestCallLog?.type || CallType.OUTGOING_TYPE,
        wasPickedUp: latestCallLog?.wasPickedUp ?? true,
        leadId: currentLead?._id || "",
        remark: callRemarks.trim() || "No remark provided",
        _id: latestCallLog?._id || undefined,
        number: latestCallLog?.number || mobileNumber || "",
      };

      await dispatch(
        addCallLogs({
          callLogs: [callLogToSave],
          leadId: currentLead?._id ?? "",
          showOnDateTelecaller: showOnDate
            ? showOnDate.toISOString()
            : undefined,
        })
      ).unwrap();

      clearCallLogStateFromStorage();

      setIsRemarkModalOpen(false);

      if (incomingLeadStack.length > 1) {
        popFromIncomingLeadStack();
      } else {
        setIncomingLeadStack([]);
        setLatestCallLog(null);
      }

      setShowOnDate(null);
      setCallRemark("");

      toast.success("Call log saved successfully!");
    } catch (error) {
      console.error("Failed to save call log:", error);
      toast.error("Failed to save call log");
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      const hasRestoredState =
        localStorage.getItem(STORAGE_KEYS.IS_MODAL_OPEN) === "true";

      if (!hasRestoredState) {
        dispatch(getTelecallerLead());
      }

      dispatch(getLogsCount({}));
    };

    initializeData();
  }, [dispatch]);

  useEffect(() => {
    if (currentLead?._id) {
      dispatch(getRemarks(currentLead._id));
    }
  }, [dispatch, currentLead?._id]);

  useEffect(() => {
    if (currentLead) {
      setAgreedToMeet(false);
    }
  }, [currentLead]);

  const handleAddRemark = async () => {
    let finalText = notPickedUp ? "not picked up" : remarkText.trim();

    const lastCall = callLogs?.length ? callLogs[callLogs.length - 1] : null;
    const callId = lastCall?._id;

    if (remarkText !== "") {
      await dispatch(
        addRemarks({
          leadId: currentLead?._id ?? "",
          text: finalText,
          callId,
        })
      );
    }
    setRemarkText("");
    setNotPickedUp(false);
    setNotInterested(false);
    setSkipped(false);
    setShowToBDE(null);
  };

  useEffect(() => {
    if (currentLead && typeof currentLead.showToBDE === "boolean") {
      setShowToBDE(currentLead.showToBDE);
    } else {
      setShowToBDE(null);
    }
  }, [currentLead]);

  const handleNextLead = async (updatedData: ILeadUpdateData) => {
    try {
      if (!currentLead) return;

      if (agreedToMeet === false && showToBDE === null) {
        toast.error("Select if you want to show to BDE or not");
        return;
      }

      await handleAddRemark();

      if (agreedToMeet === true && currentLead.status !== "follow-up") {
        updatedData = {
          ...updatedData,
          status: "agreed to meet",
          showToBDE: true,
          agreedToMeet: true,
          skipTelecallerLead: skipped,
        };
      } else {
        updatedData = {
          ...updatedData,
          agreedToMeet: false,
          skipTelecallerLead: skipped,
        };
        if ((showToBDE || agreedToMeet) && currentLead.status !== "follow-up") {
          updatedData = { ...updatedData, status: "Assigned By Telecaller" };
        }

        if (notInterested) {
          updatedData = { ...updatedData, status: "not interested" };
        }
        if (showToBDE !== null) {
          updatedData = { ...updatedData, showToBDE };
        }
      }

      const result = await dispatch(
        updateAndFetchNextLead({
          leadId: currentLead?._id ?? "",
          data: updatedData,
        })
      ).unwrap();
      dispatch(fetchKanbanData({}));

      if (result !== null) {
        toast.success("Lead updated successfully!");
      }

      setNotInterested(false);
      setNotPickedUp(false);
    } catch (error) {
      if (error !== "No Leads Available") {
        toast.error("Failed to update lead");
      }
    }
  };

  const toggleDashboard = () => {};

  useEffect(() => {
    dispatch(startSession());

    return () => {
      if (isRemarkModalOpen) {
        clearCallLogStateFromStorage();
      }
    };
  }, [dispatch, isRemarkModalOpen]);

  if (fetchingLead) {
    return <TelecallerBoardSkeleton />;
  }

  if (!fetchingLead && currentLead == null) {
    return (
      <div className="py-20 text-center text-gray-500">
        You've finished all your calls for now!
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 p-4">
      <div className="w-full max-w-full mx-auto">
        {!sessionActive && (
          <SessionInactivePage
            userLogsCount={userLogsCount}
            fetchingLead={fetchingLead}
          />
        )}

        {!sessionActive && <StartSession toggleDashboard={toggleDashboard} />}

        {sessionActive && (
          <RemarkModal
            leadInfo={currentLead!}
            handleSaveRemark={saveRemarkWithCallLog}
            remark={callRemarks}
            setRemark={setCallRemark}
            isOpen={isRemarkModalOpen}
            setIsOpen={handleModalClose}
            showOnDate={showOnDate}
            setShowOnDate={setShowOnDate}
          />
        )}
        <AnimatePresence mode="wait">
          {sessionActive && currentLead && (
            <motion.div
              key={currentLead._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 overflow-hidden"
            >
              {fetchingLead ? (
                <TelecallerBoardSkeleton />
              ) : (
                <div className="p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-6 min-h-[600px]">
                    <div className="col-span-12 sm:col-span-2 lg:col-span-3">
                      <div className="bg-white rounded-2xl border border-gray-200 shadow-lg h-full flex flex-col">
                        <CallLogs leadId={currentLead?._id ?? ""} />
                      </div>
                    </div>

                    <div className="col-span-12 sm:col-span-2 lg:col-span-6">
                      <div className="bg-white rounded-2xl border border-gray-200 shadow-lg h-full flex flex-col">
                        <div className="p-6 border-b border-gray-200 flex-shrink-0">
                          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-3">
                            <div className="p-3 bg-blue-100 rounded-xl">
                              <svg
                                className="h-5 w-5 text-blue-600"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                />
                              </svg>
                            </div>
                            Lead Information
                          </h2>
                        </div>

                        <div className="p-6 flex-1 overflow-y-auto">
                          <EditLead
                            mobileNumberTelecaller={mobileNumber!}
                            setMobileNumber={setMobileNumber!}
                            forTelecaller={true}
                            role="telecaller"
                            card={currentLead}
                            submitBtnText="Save & Next"
                            callApi={handleNextLead}
                            skipped={skipped}
                            notInterested={notInterested}
                            inline
                          />
                          <div className="mt-6">
                            <button
                              disabled={
                                callingToLeadLoading || isCallInProgress
                              }
                              onClick={callToNumber}
                              className={`${
                                callingToLeadLoading || isCallInProgress
                                  ? "opacity-50 cursor-not-allowed"
                                  : ""
                              } w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-4 px-6 rounded-xl shadow-lg transition-all duration-300 transform hover:shadow-xl`}
                            >
                              <div className="flex items-center justify-center gap-3">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-5 w-5"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                  />
                                </svg>
                                <span>
                                  {callingToLeadLoading || isCallInProgress
                                    ? "Calling..."
                                    : "Call Now"}
                                </span>
                              </div>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-span-12 sm:col-span-2 lg:col-span-3">
                      <Remarks
                        remarks={remarks || []}
                        leadId={currentLead?._id ?? ""}
                        remarkText={remarkText}
                        setRemarkText={setRemarkText}
                        notInterested={notInterested}
                        setNotInterested={setNotInterested}
                        notPickedUp={notPickedUp}
                        setNotPickedUp={setNotPickedUp}
                        skipped={skipped}
                        setSkipped={setSkipped}
                        showToBDE={showToBDE}
                        setShowToBDE={setShowToBDE}
                        agreedToMeet={agreedToMeet}
                        setAgreedToMeet={setAgreedToMeet}
                        isTelecallerContext={true}
                      />
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <CallInprogressToast isCallInProgress={isCallInProgress} />
    </div>
  );
};

export default TelecallerBoard;
